import {
  Visibility,
  VisibilityOff,
  BusinessOutlined,
  ElectricBoltOutlined,
  BarChartOutlined,
  LockOutlined,
  FlashOnOutlined,
} from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import finaraLogo from '@/assets/LP/LP-smooth-light.svg';
import { MfaVerificationModal } from '@/components/auth/MfaVerificationModal';
import { useAuth } from '@/hooks/useAuth';
import { loginSuccess } from '@/store/slices/authSlice';
import { AuthResponse, LoginForm } from '@/types/auth';

export const LoginPage: React.FC = () => {
  const { login, logout, isLoading, error, isAuthenticated, user, clearAuthError, dispatch } =
    useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
  });

  // MFA state
  const [isMfaRequired, setIsMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string>('');
  const [isCheckingMfa, setIsCheckingMfa] = useState(false);
  const [pendingAuthData, setPendingAuthData] = useState<AuthResponse | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCheckingMfa(true);

    try {
      const result = await login(formData);

      if (result.mfaRequired && result.factorId && result.authData) {
        setPendingAuthData(result.authData);
        setMfaFactorId(result.factorId);
        setIsMfaRequired(true);
        setIsCheckingMfa(false);
      } else {
        setIsCheckingMfa(false);
      }
    } catch (_error) {
      setIsCheckingMfa(false);
    }
  };

  // If MFA is required, stay on this page and show modal
  if (isMfaRequired && mfaFactorId) {
    // Don't do any redirects - just render the page with the modal
  }

  // If loading, show nothing (ProtectedRoute will handle this)
  if (isLoading) {
    return null;
  }

  // Redirect if already authenticated (but WAIT for MFA check to complete)
  if (isAuthenticated && user && !isMfaRequired && !isCheckingMfa) {
    // Block clients and employees from accessing the system
    if (user.role === 'client' || user.role === 'employee') {
      // Force logout if somehow a client or employee got authenticated
      void logout();
      return null;
    }

    if (user.role === 'super_admin') {
      return <Navigate to="/dashboard" replace />;
    } else if (user.role === 'admin' || user.role === 'manager') {
      return <Navigate to="/dashboard-empresarial" replace />;
    } else if (user.role === 'admin_onboarding') {
      return <Navigate to="/admin-onboarding/funnel" replace />;
    }
  }

  const handleMfaSuccess = () => {
    if (pendingAuthData) {
      dispatch(loginSuccess(pendingAuthData));

      // Navigate based on role
      const role = pendingAuthData.user.role;
      const redirectPath = role === 'super_admin' ? '/dashboard' : '/dashboard-empresarial';

      setIsMfaRequired(false);
      setIsCheckingMfa(false);
      void navigate(redirectPath, { replace: true });
    }
  };

  const handleMfaCancel = () => {
    setIsMfaRequired(false);
    setIsCheckingMfa(false);
    setPendingAuthData(null);
    void logout();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) clearAuthError();
  };

  return (
    <div className="login-page flex flex-col justify-center pt-4 pb-12" style={{ margin: '-21px', width: 'calc(100% + 42px)', minHeight: '100vh' }}>
      {/* Header */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-11 -translate-x-3">
          <img src={finaraLogo} alt="Finara" className="h-8 w-auto" />
        </div>
        <h2 className="text-center text-3xl font-bold text-white mb-2">Bienvenido</h2>
        <p className="text-center text-sm text-white/70">
          Inicia sesión para acceder a tu panel administrativo
        </p>
      </div>

      {/* Login Form */}
      <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-md">
        <CardContainer className="py-6 px-6 shadow-2xl shadow-black/60 bg-surface border border-black/10 dark:border-white/15">
          <form className="space-y-6" onSubmit={e => void handleSubmit(e)}>
            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="text-sm text-red-400">{error}</div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-brand-white mb-2">
                Correo electrónico
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-border bg-surface text-brand-white placeholder:text-muted rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue transition-colors"
                placeholder="tu@empresa.com"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-brand-white mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-border bg-surface text-brand-white placeholder:text-muted rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <VisibilityOff className="h-5 w-5 text-muted" />
                  ) : (
                    <Visibility className="h-5 w-5 text-muted" />
                  )}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              variant="blue"
              size="large"
              disabled={isCheckingMfa || !formData.email || !formData.password}
              startIcon={isCheckingMfa ? undefined : <ElectricBoltOutlined fontSize="inherit" />}
              className="w-full !bg-soft-balance !border-soft-balance !text-soft-digital hover:!bg-soft-balance/80 active:!bg-soft-balance/90 focus:!ring-soft-balance/50"
            >
              {isCheckingMfa ? 'Cargando...' : 'Iniciar Sesión'}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center justify-center space-x-2 text-sm text-muted">
              <BusinessOutlined fontSize="small" />
              <span>Plataforma segura para gestión empresarial</span>
            </div>
          </div>
        </CardContainer>
      </div>

      {/* Features */}
      <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="grid grid-cols-3 gap-2 md:!gap-4 text-center">
          <div className="p-2">
            <div className="w-12 h-12 bg-quick-flow/20 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-quick-flow">
                <LockOutlined />
              </span>
            </div>
            <h3 className="text-sm font-medium text-white mb-1">Seguridad Avanzada</h3>
            <p className="text-xs text-white/60">Protección de datos empresariales</p>
          </div>
          <div className="p-2">
            <div className="w-12 h-12 bg-soft-balance/20 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-soft-balance">
                <FlashOnOutlined />
              </span>
            </div>
            <h3 className="text-sm font-medium text-white mb-1">Procesamiento Rápido</h3>
            <p className="text-xs text-white/60">Operaciones en tiempo real</p>
          </div>
          <div className="p-2">
            <div className="w-12 h-12 bg-smooth-light/20 rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-smooth-light">
                <BarChartOutlined />
              </span>
            </div>
            <h3 className="text-sm font-medium text-white mb-1">Analíticas Completas</h3>
            <p className="text-xs text-white/60">Reportes y métricas detalladas</p>
          </div>
        </div>
      </div>

      {/* MFA Verification Modal */}
      {isMfaRequired && mfaFactorId && (
        <MfaVerificationModal
          isOpen={isMfaRequired}
          factorId={mfaFactorId}
          onSuccess={handleMfaSuccess}
          onCancel={handleMfaCancel}
        />
      )}
    </div>
  );
};
